import { MAutocomplete, MButton, MDialog, MIcons, MSpinner, MTextField, MTooltip, MTypography } from "@material-dev/insights-portal-ui-components";
import { useFormik } from "formik";
import { memo, SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store";

import {
  projectSelector,
  resetProject,
  setAddProjectDialogOpenedState,
  setOpenCreateWalrDataProject,
} from "slices/project/projectSlice";

import { LoadingState } from "types";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { DATASETS_PATH } from "const/routes";
import { useCreateWalrDataProjectDialogStyles } from "./CreateWalrDataProjectDialogStyles";
import { CreateWalrProjectCommand, WalrProjectDto } from "app/ApiClient";
import { createWalrDataProject, getAllWalrProjects, getWalrDatasetByProjectId, resetCreateWalrDataProjectState, resetWalrProject, walrSelector } from "slices/WalrProjects/walrProjectSlice";
import { initialValues, validationSchema } from "./CreateWalrDataProjectSchema";
import { resetProjectEncodingVersion } from "slices/projectEncodingVersion/projectEncodingVersionSlice";
import { WalrDatasetDto } from "app/SharedApiClient";
import { setIsAddNewWorkdayDatasetDialogOpenedState, setWorkdayProjectId } from "slices/workdayProjectDataset/workdayProjectDatasetSlice";
import { getProjectsPage } from "slices/projects/projectsSlice";
import { getWorkdayProjectDatasetsByWorkdayProjectId } from "slices/workdayProjectDatasets/workdayProjectDatasetsSlice";

type CreateWalrDataProjectDialogProps = {
  open: boolean;
  workdayProjectId? : number;
  onClose?: () => void;  
  isWorkdayDataset:boolean;
};

export const CreateWalrDataProjectDialog = memo(({ open, workdayProjectId, onClose, isWorkdayDataset }: CreateWalrDataProjectDialogProps) => {
  const { classes: styles } = useCreateWalrDataProjectDialogStyles();
  const dispatch = useAppDispatch();
  const { isAddProjectDialogOpened } = useSelector(projectSelector);
  const { createWalrDataProjectState, createWalrProject } = useAppSelector((state) => state.walrProject);
  const { data: walrProjects, processing: isOriginProjectsLoading, getDatasetsLoadingState, projectDatasets } = useSelector(walrSelector);
  const [selectedWalrProjectId, setSelectedWalrProjectId] = useState<string>();
  const [existingDataset, setExistingDataset] = useState<boolean>(false);
  const [projectId, setProjectId] = useState<number>();
  const [selectedWalrDatasetId, setSelectedWalrDatasetId] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
      dispatch(getAllWalrProjects());
      setExistingDataset(false);
      setSelectedWalrProjectId(undefined);
    }, [open]);
    
  const handleSubmit = useCallback(
    (values: CreateWalrProjectCommand) => {
      dispatch(
        createWalrDataProject({
          ...values,
          workdayProjectId: workdayProjectId,
        }),
      )
        .then(({ payload }) => {
          const res = payload as WalrProjectDto;
          if (res.isDatasetExists) {
            setExistingDataset(true);
            setProjectId(res.id);
          } else {
            dispatch(resetProjectEncodingVersion());
            dispatch(resetProject());
            if (isWorkdayDataset && workdayProjectId) {
              closeDialog();
              dispatch(setIsAddNewWorkdayDatasetDialogOpenedState(false));
              dispatch(getWorkdayProjectDatasetsByWorkdayProjectId({ workdayProjectId: workdayProjectId, forceFetch: true }));
              navigate(`/project/${workdayProjectId}`);
              dispatch(setWorkdayProjectId());
            }
            else {
              dispatch(getProjectsPage());
              navigate(DATASETS_PATH);
            }
          }
          return;
        })
        .catch((e) => {
          console.error(e);
          return;
        });
    },
    [onsubmit, isWorkdayDataset, workdayProjectId],
  );

  const formik = useFormik<CreateWalrProjectCommand>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
  });

  useEffect(() => {        
        dispatch(getWalrDatasetByProjectId(selectedWalrProjectId??""));
    }, [selectedWalrProjectId, dispatch])

   const handleWalrDatasetIdChanged = useCallback(
      (_: SyntheticEvent<Element, Event>, value: WalrDatasetDto | null) => {       
        formik.setValues({...formik.values, datasetId: value?.id??"", datasetName: value?.name??""});        
        if (value && value.name) {
          setSelectedWalrDatasetId(value.name);
        }
      },
      [formik, setSelectedWalrDatasetId],
    );
  
    const handleExternalProjectChange = useCallback(
      (_: SyntheticEvent<Element, Event>, value: string | undefined) => {
        formik.setFieldValue("projectId", value);        
        setSelectedWalrProjectId(value)
      },
      [formik, setSelectedWalrProjectId],
    );

  const closeDialog = useCallback(() => {
    if (isWorkdayDataset && onClose) {
      onClose();
    } else {
      dispatch(setOpenCreateWalrDataProject(false));
      dispatch(setAddProjectDialogOpenedState(true));
    }
    dispatch(resetWalrProject());
  }, [dispatch]);

  const getTitle = useCallback(
    () => (
      <div>
        <MButton className={styles.btnBack} onClick={closeDialog}>
          <MIcons.ArrowBack className={styles.backIcon} />
          Go back
        </MButton>
        {!existingDataset && <div className={styles.title}>Add a Walr dataset</div>}
      </div>
    ),
    [existingDataset],
  );

  useEffect(() => {
    formik.resetForm();
  }, [isAddProjectDialogOpened]);

  useEffect(() => {
    if (createWalrDataProjectState === LoadingState.succeed && !createWalrProject?.isDatasetExists) {
      dispatch(resetCreateWalrDataProjectState());
      dispatch(setOpenCreateWalrDataProject(false));
      dispatch(setAddProjectDialogOpenedState(false));
    }
  }, [createWalrDataProjectState, dispatch, createWalrProject]);

  const handleCloseClick = useCallback(() => {
    if (isWorkdayDataset && onClose) {
      onClose();
    } else {    
    dispatch(setOpenCreateWalrDataProject(false));
    }
  }, [dispatch]);

    const getExistingDatasetMessages = (datasetId: string) => (
      <div>No action has been taken because the dataset ID {datasetId} already exists in this project. </div>
    );

    const handleOpenExistingDatasetClick = useCallback(() => {
      dispatch(resetProjectEncodingVersion());
      dispatch(resetProject());
      navigate(`${DATASETS_PATH}/${projectId ?? ""}`)
    }, [dispatch, projectId]);

    const getActions = useMemo(() => {
      if (existingDataset) {
        return (
          <div className={styles.actions}>
            <MButton
              variant="outlined"
              color="primary"
              type="submit"
              size="small"
              onClick={handleOpenExistingDatasetClick}>
              Open Existing Dataset
            </MButton>
          </div>
        );
      }

      if (!existingDataset) {
        return (
          <div className={styles.actions}>
            <MButton
              color="primary"
              variant="outlined"
              onClick={closeDialog}
              disabled={createWalrDataProjectState === LoadingState.pending}>
              Back to Data Source
            </MButton>
            <MButton
              type="submit"
              color="primary"
              variant="contained"
              form="create-walr-data-project-form"
              disabled={
                createWalrDataProjectState === LoadingState.pending || Boolean(!(formik.isValid && formik.dirty))
              }>
              Add Dataset
              {createWalrDataProjectState === LoadingState.pending && (
                <MSpinner isLoading={createWalrDataProjectState === LoadingState.pending} className={styles.spinner} />
              )}
            </MButton>
          </div>
        );
      }
    }, [formik, createWalrDataProjectState, existingDataset]);

    return (
      <>
        <MDialog titleElement={getTitle()} actionsElement={getActions} open={open} onClose={handleCloseClick} fullWidth>
          {existingDataset && selectedWalrDatasetId && (
            <div className={styles.message}>
              <div className={styles.messageTitle}>Dataset already exists!</div>
              <div className={styles.messageBody}>{getExistingDatasetMessages(selectedWalrDatasetId)}</div>
            </div>
          )}
          <form id="create-walr-data-project-form" onSubmit={formik.handleSubmit} autoComplete="off">
            {!existingDataset && (
              <div className={styles.root}>
                <div className={styles.formItem}>
                  <div className={styles.label}>Walr project</div>
                  <MAutocomplete
                    options={walrProjects ?? []}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, value) => {
                      handleExternalProjectChange(event, value?.id);
                    }}
                    className={styles.autoComplete}
                    virtual
                    loading={isOriginProjectsLoading}
                    renderInput={(params) => (
                      <MTextField
                        {...params}
                        placeholder="Select a Walr project"
                        variant="outlined"
                        name="projectId"
                        error={Boolean(formik.errors.projectId && formik.touched.projectId)}
                        helperText={
                            Boolean(formik.errors.projectId && formik.touched.projectId)
                            ? formik.errors.projectId
                            : ""
                        }
                        />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <MTooltip classes={{ tooltip: styles.optionTooltip }} title={option.name}>
                          <MTypography noWrap>{option.name}</MTypography>
                        </MTooltip>
                      </li>
                    )}
                  />
                </div>
                <div className={styles.formItem}>
                  <div className={styles.label}>Walr dataset</div>
                  <MAutocomplete
                    options={projectDatasets ?? []}
                    getOptionLabel={(option) => (option?.name ?? "")}
                    onChange={(event, value) => {
                      handleWalrDatasetIdChanged(event, value);
                    }}
                    className={styles.autoComplete}
                    virtual
                    noOptionsText="It looks like there aren’t any datasets available for this project on Walr just yet. This might be because the project is new and datasets haven’t been added."
                    loading={getDatasetsLoadingState === LoadingState.pending}
                    renderInput={(params) => (
                      <MTextField
                        {...params}
                        placeholder="Select a Walr dataset"
                        variant="outlined"
                        name="walrDataset"
                        error={Boolean(formik.errors.datasetId && formik.touched.datasetId)}
                        helperText={
                            Boolean(formik.errors.datasetId && formik.touched.datasetId)
                            ? formik.errors.datasetId
                            : ""
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <MTooltip classes={{ tooltip: styles.optionTooltip }} title={option.name}>
                          <MTypography noWrap>{option.name}</MTypography>
                        </MTooltip>
                      </li>
                    )}
                  />
                </div>
              </div>
            )}
          </form>
        </MDialog>
      </>
    );
  },
);
